/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { CodedError } from '@karya/error-values';

/**
 * Centralized error classes for platform components
 * Provides consistent, reusable error classes across all platform components
 */

// ============================================================================
// Media Loading Errors
// ============================================================================

type MediaType = 'audio' | 'video' | 'image';

type MediaLoadErrorDetails = {
  mediaType: MediaType;
  url?: string;
  originalError?: unknown;
};

type MediaFormatErrorDetails = {
  mediaType: MediaType;
  url?: string;
  expectedFormat?: string;
};

type MediaNetworkErrorDetails = {
  mediaType: MediaType;
  url?: string;
  statusCode?: number;
};

type MediaDecodeErrorDetails = {
  mediaType: MediaType;
  url?: string;
  originalError?: unknown;
};

export class MediaLoadError extends CodedError<'MediaLoadError', MediaLoadErrorDetails> {
  constructor(mediaType: MediaType, url?: string, originalError?: unknown) {
    super('MediaLoadError', `Failed to load ${mediaType}. Please check the file and try again.`, {
      mediaType,
      url,
      originalError,
    });
  }
}

export class MediaFormatError extends CodedError<'MediaFormatError', MediaFormatErrorDetails> {
  constructor(mediaType: MediaType, url?: string, expectedFormat?: string) {
    const formatMessage = expectedFormat ? ` Expected format: ${expectedFormat}.` : '';
    super('MediaFormatError', `Invalid ${mediaType} format.${formatMessage} Please use a supported format.`, {
      mediaType,
      url,
      expectedFormat,
    });
  }
}

export class MediaNetworkError extends CodedError<'MediaNetworkError', MediaNetworkErrorDetails> {
  constructor(mediaType: MediaType, url?: string, statusCode?: number) {
    const statusMessage = statusCode ? ` (HTTP ${statusCode})` : '';
    super(
      'MediaNetworkError',
      `Network error while loading ${mediaType}.${statusMessage} Please check your connection and try again.`,
      { mediaType, url, statusCode },
    );
  }
}

export class MediaDecodeError extends CodedError<'MediaDecodeError', MediaDecodeErrorDetails> {
  constructor(mediaType: MediaType, url?: string, originalError?: unknown) {
    super('MediaDecodeError', `Failed to decode ${mediaType}. The file may be corrupted or in an unsupported format.`, {
      mediaType,
      url,
      originalError,
    });
  }
}

/**
 * Media loading error factory methods
 */
export const PlatformMediaErrors = {
  getMediaLoadError: (mediaType: MediaType, url?: string, originalError?: unknown): MediaLoadError => {
    return new MediaLoadError(mediaType, url, originalError);
  },

  getMediaFormatError: (mediaType: MediaType, url?: string, expectedFormat?: string): MediaFormatError => {
    return new MediaFormatError(mediaType, url, expectedFormat);
  },

  getMediaNetworkError: (mediaType: MediaType, url?: string, statusCode?: number): MediaNetworkError => {
    return new MediaNetworkError(mediaType, url, statusCode);
  },

  getMediaDecodeError: (mediaType: MediaType, url?: string, originalError?: unknown): MediaDecodeError => {
    return new MediaDecodeError(mediaType, url, originalError);
  },

  /**
   * Get error from media element error event
   */
  getMediaElementError: (
    error: unknown,
    mediaType: MediaType,
    url?: string,
  ): MediaLoadError | MediaNetworkError | MediaDecodeError | MediaFormatError => {
    if (error instanceof Event) {
      const target = error.target;
      if (target instanceof HTMLMediaElement) {
        const mediaError = target.error;
        if (mediaError) {
          switch (mediaError.code) {
            case mediaError.MEDIA_ERR_ABORTED:
              return new MediaLoadError(mediaType, url, error);
            case mediaError.MEDIA_ERR_NETWORK:
              return new MediaNetworkError(mediaType, url);
            case mediaError.MEDIA_ERR_DECODE:
              return new MediaDecodeError(mediaType, url, error);
            case mediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
              return new MediaFormatError(mediaType, url);
            default:
              return new MediaLoadError(mediaType, url, error);
          }
        }
      }
    }
    return new MediaLoadError(mediaType, url, error);
  },
};

// ============================================================================
// File URL Resolution Errors
// ============================================================================

type FileUrlResolutionErrorDetails = {
  fileKey?: string;
  fileUrlMap?: Record<string, string>;
};

type MissingFileErrorDetails = {
  fileKey?: string;
  fileType?: string;
};

type InvalidFileUrlErrorDetails = {
  url?: string;
  reason?: string;
};

export class FileUrlResolutionError extends CodedError<'FileUrlResolutionError', FileUrlResolutionErrorDetails> {
  constructor(fileKey?: string, fileUrlMap?: Record<string, string>) {
    super('FileUrlResolutionError', `Failed to resolve file URL. Please check the file configuration.`, {
      fileKey,
      fileUrlMap,
    });
  }
}

export class MissingFileError extends CodedError<'MissingFileError', MissingFileErrorDetails> {
  constructor(fileKey?: string, fileType?: string) {
    const typeMessage = fileType ? ` (${fileType})` : '';
    super('MissingFileError', `File not found${typeMessage}. Please ensure the file exists and is accessible.`, {
      fileKey,
      fileType,
    });
  }
}

export class InvalidFileUrlError extends CodedError<'InvalidFileUrlError', InvalidFileUrlErrorDetails> {
  constructor(url?: string, reason?: string) {
    const reasonMessage = reason ? ` ${reason}` : '';
    super('InvalidFileUrlError', `Invalid file URL.${reasonMessage} Please provide a valid file URL.`, { url, reason });
  }
}

/**
 * File URL resolution error factory methods
 */
export const PlatformFileErrors = {
  getFileUrlResolutionError: (fileKey?: string, fileUrlMap?: Record<string, string>): FileUrlResolutionError => {
    return new FileUrlResolutionError(fileKey, fileUrlMap);
  },

  getMissingFileError: (fileKey?: string, fileType?: string): MissingFileError => {
    return new MissingFileError(fileKey, fileType);
  },

  getInvalidFileUrlError: (url?: string, reason?: string): InvalidFileUrlError => {
    return new InvalidFileUrlError(url, reason);
  },
};

// ============================================================================
// Network Errors
// ============================================================================

type NetworkTimeoutErrorDetails = {
  url?: string;
  timeout?: number;
};

type NetworkConnectionErrorDetails = {
  url?: string;
  originalError?: unknown;
};

export class NetworkTimeoutError extends CodedError<'NetworkTimeoutError', NetworkTimeoutErrorDetails> {
  constructor(url?: string, timeout?: number) {
    const timeoutMessage = timeout ? ` (timeout: ${timeout}ms)` : '';
    super(
      'NetworkTimeoutError',
      `Network request timed out${timeoutMessage}. Please check your connection and try again.`,
      { url, timeout },
    );
  }
}

export class NetworkConnectionError extends CodedError<'NetworkConnectionError', NetworkConnectionErrorDetails> {
  constructor(url?: string, originalError?: unknown) {
    super('NetworkConnectionError', `Network connection failed. Please check your internet connection and try again.`, {
      url,
      originalError,
    });
  }
}

/**
 * Network error factory methods
 */
export const PlatformNetworkErrors = {
  getNetworkTimeoutError: (url?: string, timeout?: number): NetworkTimeoutError => {
    return new NetworkTimeoutError(url, timeout);
  },

  getNetworkConnectionError: (url?: string, originalError?: unknown): NetworkConnectionError => {
    return new NetworkConnectionError(url, originalError);
  },
};

// ============================================================================
// Generic Platform Errors
// ============================================================================

type ComponentRenderErrorDetails = {
  componentType?: string;
  originalError?: unknown;
};

type AutoAdvanceErrorDetails = {
  componentType?: string;
  delay?: number;
};

export class ComponentRenderError extends CodedError<'ComponentRenderError', ComponentRenderErrorDetails> {
  constructor(componentType?: string, originalError?: unknown) {
    const typeMessage = componentType ? ` (${componentType})` : '';
    super('ComponentRenderError', `Failed to render component${typeMessage}. Please try refreshing the page.`, {
      componentType,
      originalError,
    });
  }
}

export class AutoAdvanceError extends CodedError<'AutoAdvanceError', AutoAdvanceErrorDetails> {
  constructor(componentType?: string, delay?: number) {
    const delayMessage = delay ? ` after ${delay}ms` : '';
    super('AutoAdvanceError', `Failed to auto-advance component${delayMessage}. Please continue manually.`, {
      componentType,
      delay,
    });
  }
}

/**
 * Generic platform error factory methods
 */
export const PlatformGenericErrors = {
  getComponentRenderError: (componentType?: string, originalError?: unknown): ComponentRenderError => {
    return new ComponentRenderError(componentType, originalError);
  },

  getAutoAdvanceError: (componentType?: string, delay?: number): AutoAdvanceError => {
    return new AutoAdvanceError(componentType, delay);
  },
};
